import { FC, useId } from "react";
import Modal from "@/components/modal";
import AnchorDataForm, { AnchorField } from "@/components/anchorData/anchorDataForm";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { api } from "@/helper/api";
import { getAnchorDataBulkCreateUpdateErrors } from "@/helper/anchorDataBulkCreateUpdate.utils";
import useFormSubmission from "@/helper/customHooks/useFormSubmission";

interface SubjectOverviewAnchorDataCardModalProps {
  formFields: any;
  open: boolean;
  onClose: (state: boolean) => void;
  handleError: (value: any) => void;
  handleNotificationMessage: (value: any) => void;
  handleNotificationVariant: (value: string) => void;
  handleShowNotification: (state: boolean) => void;
  subjectId: any;
  onSuccessfullSubmit: (subjectId: any) => void;
}

type GenericObject = Record<string, string> | Record<string, any>;

const SubjectOverviewAnchorDataCardModal: FC<
  SubjectOverviewAnchorDataCardModalProps
> = ({
  formFields,
  open,
  onClose,
  handleError,
  handleNotificationMessage,
  handleNotificationVariant,
  handleShowNotification,
  subjectId,
  onSuccessfullSubmit,
}) => {
  const { t } = useTranslation();
  const formId = useId();
  const modalForm = useForm<any>({
    mode: "onChange",
    defaultValues: {},
  });

  const handleModalSubmit = async (data: GenericObject) => {
    const filtered = filterOutNonEditable(data, formFields);

    const dataToSubmit = [{
      subject: subjectId,
      anchor_data_values: filtered,
    }];

    try {
      const res: any = await new Promise<any>((resolve, reject) => {
        api.genericApiRequest({
          method: "post",
          entity: "anchorData",
          additionalRouteParts: { bulk_create_update: "/" },
          parametersToRender: {},
          submitData: dataToSubmit,
          successHandler: (res: any) => resolve(res),
          failHandler: (error: any) => reject(error),
        });
      });
      const bulkErrors = getAnchorDataBulkCreateUpdateErrors(res, {
        submittedSubjectIds: subjectId ? [subjectId] : [],
      });
      if (bulkErrors.length > 0) {
        throw bulkErrors;
      }

      modalForm.reset(data);

      handleNotificationMessage(t("save_successful"));
      handleNotificationVariant("success");
      handleShowNotification(true);
      onClose(false);
      onSuccessfullSubmit(subjectId);
    } catch (error) {
      handleError(error);
    }
  };

  const { handleSubmit: submit, isSubmitting } = useFormSubmission({
    onSubmit: modalForm.handleSubmit(handleModalSubmit),
    disabled: !open,
  });

  const modalContent = (
    <FormProvider {...modalForm}>
      <form
        id={formId}
        onSubmit={submit}
        noValidate // Keep React Hook Form as the sole validator
      >
        <AnchorDataForm fields={formFields} mode="modal" />
      </form>
    </FormProvider>
  );
  return (
    <Modal
      title={t("edit_anchordata")}
      open={open}
      onClose={() => onClose(false)}
      submitFormId={formId}
      isSubmitting={isSubmitting}
    >
      {modalContent}
    </Modal>
  );
};

export default SubjectOverviewAnchorDataCardModal;

function filterOutNonEditable(data: GenericObject, formFields: AnchorField[]) {
  const byName = new Map(formFields.map((f) => [f.name, f.editable]));
  const byType = new Map(formFields.map((f) => [f.name, f.type]));

  const outgoingFields: GenericObject = {};

  for (const [key, value] of Object.entries(data)) {
    const editable = byName.get(key);
    const fieldType = byType.get(key);
    if (editable === false) continue;

    let transformedValue = value;

    if (fieldType === "object_list" || fieldType === "subject_id_list") {
      if (transformedValue === "") {
        transformedValue = [];
      }
    }

    if (
      fieldType === "int" ||
      fieldType === "float" ||
      fieldType === "percent"
    ) {
      if (transformedValue === "") {
        transformedValue = 0;
      }
    }

    if (Array.isArray(transformedValue)) {
      if (transformedValue.every((v) => v && "value" in v)) {
        transformedValue = transformedValue.map((v) => v.value);
      } else if (transformedValue.every((v) => v && "id" in v)) {
        transformedValue = transformedValue.map((v) => v.id);
      }
    }

    if (transformedValue === null) {
      outgoingFields[key] = null;
    } else if (transformedValue !== undefined) {
      outgoingFields[key] = {
        value: transformedValue,
      };
    }
  }

  return outgoingFields;
}
